import { useEffect, useRef } from "react";

const SCROLL_IDLE_MS = 700;
const AXIS_EPSILON = 0.5;
const BAR_THICKNESS = 4;
const BAR_INSET = 5;
const MIN_THUMB_SIZE = 32;

type ActiveScroll = {
  scroller: HTMLElement;
  showY: boolean;
  showX: boolean;
};

const isRootScroller = (el: HTMLElement) =>
  el === document.documentElement || el === document.body;

const getScroller = (target: EventTarget | null): HTMLElement | null => {
  if (!target || target === document) return document.documentElement;
  if (target instanceof HTMLElement) return target;
  return null;
};

/**
 * Whether the scroller is still part of the rendered page. Returns false once
 * a modal/dropdown unmounts it or hides it via `display`/`visibility`.
 */
const isRendered = (el: HTMLElement) => {
  if (!el.isConnected) return false;
  if (typeof el.checkVisibility === "function") {
    return el.checkVisibility({ checkVisibilityCSS: true });
  }
  return el.getClientRects().length > 0;
};

/**
 * Combined opacity of the scroller and all of its ancestors. Lets the bar
 * follow a parent's fade-out (e.g. MUI Dialog `Fade`, Menu `Grow`).
 */
const getEffectiveOpacity = (el: HTMLElement) => {
  let opacity = 1;
  let node: HTMLElement | null = el;
  while (node && node !== document.documentElement) {
    const value = parseFloat(getComputedStyle(node).opacity);
    if (!Number.isNaN(value)) opacity *= value;
    if (opacity <= 0) return 0;
    node = node.parentElement;
  }
  return opacity;
};

const getMetrics = (scroller: HTMLElement) => {
  const root = isRootScroller(scroller);
  const rect = root
    ? { top: 0, left: 0, right: window.innerWidth, bottom: window.innerHeight }
    : scroller.getBoundingClientRect();

  return {
    root,
    rect,
    scrollTop: root ? window.scrollY : scroller.scrollTop,
    scrollLeft: root ? window.scrollX : scroller.scrollLeft,
    scrollHeight: root
      ? document.documentElement.scrollHeight
      : scroller.scrollHeight,
    scrollWidth: root
      ? document.documentElement.scrollWidth
      : scroller.scrollWidth,
    clientHeight: root ? window.innerHeight : scroller.clientHeight,
    clientWidth: root ? window.innerWidth : scroller.clientWidth,
  };
};

const getThumb = (
  scrollPos: number,
  scrollSize: number,
  clientSize: number
) => {
  const size = Math.max(MIN_THUMB_SIZE, (clientSize / scrollSize) * clientSize);
  const maxOffset = Math.max(0, scrollSize - clientSize);
  const offset = maxOffset > 0 ? (scrollPos / maxOffset) * (clientSize - size) : 0;
  return { size, offset };
};

const placeBar = (
  bar: HTMLDivElement,
  top: number,
  left: number,
  width: number,
  height: number
) => {
  bar.style.top = `${top}px`;
  bar.style.left = `${left}px`;
  bar.style.width = `${width}px`;
  bar.style.height = `${height}px`;
};

const ScrollFadeBar = () => {
  const yBarRef = useRef<HTMLDivElement>(null);
  const xBarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const yBar = yBarRef.current;
    const xBar = xBarRef.current;
    if (!yBar || !xBar) return;

    const bars = [yBar, xBar];
    const lastPos = new WeakMap<HTMLElement, { top: number; left: number }>();
    let active: ActiveScroll | null = null;
    let timeoutId = 0;
    let frameId = 0;

    const setInstant = (instant: boolean) => {
      bars.forEach((bar) => bar.classList.toggle("is-instant", instant));
    };

    const setOpacity = (opacity: number) => {
      bars.forEach((bar) =>
        bar.style.setProperty("--scroll-fade-bar-opacity", String(opacity))
      );
    };

    const hide = (instant = false) => {
      active = null;
      window.cancelAnimationFrame(frameId);
      frameId = 0;
      // When the scroller vanished with its parent there is nothing left to
      // fade against, so drop the bar in the same frame instead of lingering.
      setInstant(instant);
      bars.forEach((bar) => bar.classList.remove("is-visible"));
    };

    const scheduleHide = () => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => hide(), SCROLL_IDLE_MS);
    };

    /** Sync bar position/opacity with the active scroller's current state. */
    const render = () => {
      if (!active) return;
      const { scroller, showY, showX } = active;
      const root = isRootScroller(scroller);

      if (!root && !isRendered(scroller)) {
        window.clearTimeout(timeoutId);
        hide(true);
        return;
      }

      const m = getMetrics(scroller);
      const opacity = root ? 1 : getEffectiveOpacity(scroller);

      // While a parent is mid-fade, follow it exactly rather than easing.
      setInstant(opacity < 1);
      setOpacity(opacity);

      if (showY) {
        const { size, offset } = getThumb(
          m.scrollTop,
          m.scrollHeight,
          m.clientHeight
        );
        placeBar(
          yBar,
          m.rect.top + offset,
          m.rect.right - BAR_INSET,
          BAR_THICKNESS,
          size
        );
      }

      if (showX) {
        const { size, offset } = getThumb(
          m.scrollLeft,
          m.scrollWidth,
          m.clientWidth
        );
        placeBar(
          xBar,
          m.rect.bottom - BAR_INSET,
          m.rect.left + offset,
          size,
          BAR_THICKNESS
        );
      }
    };

    const tick = () => {
      render();
      frameId = active ? window.requestAnimationFrame(tick) : 0;
    };

    const onScroll = (event: Event) => {
      const scroller = getScroller(event.target);
      if (
        !scroller ||
        scroller.classList.contains("scroll-fade-bar") ||
        scroller.classList.contains("native-scrollbar")
      ) {
        return;
      }

      const m = getMetrics(scroller);
      const overflowY = m.scrollHeight > m.clientHeight + 1;
      const overflowX = m.scrollWidth > m.clientWidth + 1;
      const prev = lastPos.get(scroller);
      lastPos.set(scroller, { top: m.scrollTop, left: m.scrollLeft });

      const movedY = prev
        ? Math.abs(m.scrollTop - prev.top) > AXIS_EPSILON
        : overflowY && !overflowX;
      const movedX = prev
        ? Math.abs(m.scrollLeft - prev.left) > AXIS_EPSILON
        : overflowX && !overflowY;
      const showY = overflowY && (movedY || (!prev && !movedX));
      const showX = overflowX && movedX;

      if (!showY && !showX) {
        hide();
        return;
      }

      active = { scroller, showY, showX };
      setInstant(false);
      yBar.classList.toggle("is-visible", showY);
      xBar.classList.toggle("is-visible", showX);
      render();

      if (active && !frameId) {
        frameId = window.requestAnimationFrame(tick);
      }
      scheduleHide();
    };

    window.addEventListener("scroll", onScroll, { capture: true, passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.clearTimeout(timeoutId);
      hide();
    };
  }, []);

  return (
    <>
      <div ref={yBarRef} className="scroll-fade-bar" aria-hidden />
      <div ref={xBarRef} className="scroll-fade-bar" aria-hidden />
    </>
  );
};

export default ScrollFadeBar;
